import React, { useState, useRef } from 'react';
import { Modal, Button, Space, Typography, Card, Alert } from 'antd';
import {
  SaveOutlined,
  CloseOutlined,
  PlayCircleOutlined,
} from '@ant-design/icons';

const { Text, Title } = Typography;

interface VideoPreviewModalProps {
  visible: boolean;
  videoFile: File;
  videoUrl: string;
  onConfirm: (file: File) => void;
  onCancel: () => void;
}

const VideoPreviewModal: React.FC<VideoPreviewModalProps> = ({
  visible,
  videoFile,
  videoUrl,
  onConfirm,
  onCancel,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoInfo, setVideoInfo] = useState<{
    duration: number;
    width: number;
    height: number;
  } | null>(null);

  const handleVideoLoad = () => {
    if (videoRef.current) {
      setVideoInfo({
        duration: videoRef.current.duration,
        width: videoRef.current.videoWidth,
        height: videoRef.current.videoHeight,
      });
    }
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const handleConfirm = () => {
    onConfirm(videoFile);
  };

  return (
    <Modal
      title={
        <Title level={4} style={{ margin: 0 }}>
          <PlayCircleOutlined /> Preview Video
        </Title>
      }
      open={visible}
      onCancel={onCancel}
      width={800}
      centered
      footer={[
        <Button key="cancel" onClick={onCancel} icon={<CloseOutlined />}>
          Cancel
        </Button>,
        <Button
          key="confirm"
          type="primary"
          onClick={handleConfirm}
          icon={<SaveOutlined />}
        >
          Confirm & Upload
        </Button>,
      ]}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        {/* Video Preview */}
        <Card>
          <div
            style={{
              width: '100%',
              backgroundColor: '#000',
              borderRadius: 8,
              overflow: 'hidden',
            }}
          >
            <video
              ref={videoRef}
              src={videoUrl}
              controls
              onLoadedMetadata={handleVideoLoad}
              style={{
                width: '100%',
                maxHeight: 400,
                display: 'block',
              }}
            />
          </div>
        </Card>

        {/* Video Info */}
        {videoInfo && (
          <Card title="Video Information" size="small">
            <Space direction="vertical" style={{ width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text type="secondary">File Name:</Text>
                <Text strong>{videoFile.name}</Text>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text type="secondary">File Size:</Text>
                <Text strong>{formatFileSize(videoFile.size)}</Text>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text type="secondary">Duration:</Text>
                <Text strong>{formatDuration(videoInfo.duration)}</Text>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text type="secondary">Resolution:</Text>
                <Text strong>
                  {videoInfo.width} x {videoInfo.height}
                </Text>
              </div>
            </Space>
          </Card>
        )}

        {/* Size Warning */}
        {videoFile.size > 50 * 1024 * 1024 && (
          <Alert
            message="File Size Warning"
            description="This video is larger than 50MB. Upload may fail or take a long time. Consider compressing the video first."
            type="warning"
            showIcon
          />
        )}

        {/* Instructions */}
        <Card size="small" style={{ backgroundColor: '#f0f5ff', borderColor: '#adc6ff' }}>
          <Space direction="vertical" size="small">
            <Text strong>💡 Tips for Best Results:</Text>
            <Text type="secondary">• Keep videos under 50MB for faster loading</Text>
            <Text type="secondary">• Recommended: 30-60 seconds duration</Text>
            <Text type="secondary">• Use MP4 format for best compatibility</Text>
            <Text type="secondary">• Videos should showcase the service clearly</Text>
          </Space>
        </Card>
      </Space>
    </Modal>
  );
};

export default VideoPreviewModal;
